import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    TextField,
    Stack,
    Paper,
    InputAdornment,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Select,
    MenuItem,
    FormControl,
    FormHelperText
} from '@mui/material';
import {
    Add as AddIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    FileDownload as FileDownloadIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const AvgScreenerFilterSystem = ({ onSearch, onClear, onExport, showExport = false, exchanges = [] }) => {
    const theme = useTheme();

    const [availableFilters, setAvailableFilters] = useState([]);
    const [activeFilters, setActiveFilters] = useState([]);
    const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilters, setSelectedFilters] = useState(new Set());

    const predefinedRanges = {
        marketCap: {
            above: [
                { label: 'Above 1M', value: 1000000 },
                { label: 'Above 10M', value: 10000000 },
                { label: 'Above 100M', value: 100000000 },
                { label: 'Above 500M', value: 500000000 },
                { label: 'Above 1B', value: 1000000000 },
                { label: 'Above 10B', value: 10000000000 },
                { label: 'Above 50B', value: 50000000000 },
                { label: 'Above 100B', value: 100000000000 },
                { label: 'Above 300B', value: 300000000000 },
                { label: 'Above 500B', value: 500000000000 },
                { label: 'Above 1T', value: 1000000000000 }
            ],
            below: [
                { label: 'Under 1M', value: 1000000 },
                { label: 'Under 10M', value: 10000000 },
                { label: 'Under 100M', value: 100000000 },
                { label: 'Under 500M', value: 500000000 },
                { label: 'Under 1B', value: 1000000000 },
                { label: 'Under 10B', value: 10000000000 },
                { label: 'Under 50B', value: 50000000000 },
                { label: 'Under 100B', value: 100000000000 },
                { label: 'Under 300B', value: 300000000000 },
                { label: 'Under 500B', value: 500000000000 },
                { label: 'Under 1T', value: 1000000000000 }
            ]
        },
        price: {
            above: [
                { label: 'Above $1', value: 1 },
                { label: 'Above $5', value: 5 },
                { label: 'Above $10', value: 10 },
                { label: 'Above $20', value: 20 },
                { label: 'Above $50', value: 50 },
                { label: 'Above $100', value: 100 },
                { label: 'Above $200', value: 200 },
                { label: 'Above $500', value: 500 }
            ],
            below: [
                { label: 'Under $1', value: 1 },
                { label: 'Under $5', value: 5 },
                { label: 'Under $10', value: 10 },
                { label: 'Under $20', value: 20 },
                { label: 'Under $50', value: 50 },
                { label: 'Under $100', value: 100 },
                { label: 'Under $200', value: 200 },
                { label: 'Under $500', value: 500 }
            ]
        }
    };
    
    useEffect(() => {
        setAvailableFilters([
            {
                id: 'averageComparison',
                label: 'Average Comparison',
                type: 'composite',
                components: {
                    averageType: {
                        label: 'Average Type',
                        options: [
                            { value: '50', label: '50-Day Average' },
                            { value: '200', label: '200-Day Average' }
                        ]
                    },
                    comparisonType: {
                        label: 'Comparison Type',
                        options: [
                            { value: 'range', label: 'Within Range' },
                            { value: 'above', label: 'Above' },
                            { value: 'below', label: 'Below' }
                        ]
                    },
                    percentageRange: {
                        label: 'Percentage Range',
                        options: [
                            { value: '-30', label: '-30%' },
                            { value: '-20', label: '-20%' },
                            { value: '-10', label: '-10%' },
                            { value: '-5', label: '-5%' },
                            { value: '0', label: '0%' },
                            { value: '5', label: '5%' },
                            { value: '10', label: '10%' },
                            { value: '20', label: '20%' },
                            { value: '30', label: '30%' }
                        ]
                    }
                }
            },
            { id: 'price', label: 'Price', type: 'range', hasPresetRanges: true },
            { id: 'marketCap', label: 'Market Cap', type: 'range', hasPresetRanges: true },
            { id: 'exchange', label: 'Exchange', type: 'select', options: exchanges }
        ]);
    }, [exchanges]);

    const toggleFilter = (filter) => {
        const newSelectedFilters = new Set(selectedFilters);

        if (newSelectedFilters.has(filter.id)) {
            newSelectedFilters.delete(filter.id);
            setActiveFilters(activeFilters.filter(f => f.id !== filter.id));
        } else {
            newSelectedFilters.add(filter.id);
            if (!activeFilters.find(f => f.id === filter.id)) {
                let initialValues;
                if (filter.type === 'composite' && filter.id === 'averageComparison') {
                    initialValues = {
                        averageType: '50',
                        comparisonType: 'range',
                        minPercentage: '0',
                        maxPercentage: '5'
                    };
                } else if (filter.type === 'range') {
                    initialValues = { min: '', max: '' };
                } else {
                    initialValues = '';
                }

                const newFilter = {
                    ...filter,
                    values: initialValues
                };
                setActiveFilters([...activeFilters, newFilter]);
            }
        }

        setSelectedFilters(newSelectedFilters);
    };

    const handleFilterValueChange = (filterId, value, field, component = null) => {
        setActiveFilters(activeFilters.map(filter => {
            if (filter.id === filterId) {
                if (filter.type === 'composite') {
                    let newValues = { ...filter.values };

                    if (component === 'comparisonType') {
                        newValues = {
                            ...newValues,
                            [component]: value,
                            minPercentage: '0',
                            maxPercentage: value === 'range' ? '5' : '0'
                        };
                    } else {
                        newValues[component] = value;
                    }

                    return {
                        ...filter,
                        values: newValues
                    };
                } else if (filter.type === 'range') {
                    return {
                        ...filter,
                        values: {
                            ...filter.values,
                            [field]: value
                        }
                    };
                } else {
                    return {
                        ...filter,
                        values: value
                    };
                }
            }
            return filter;
        }));
    };

    const handleSearch = () => {
        const filterData = activeFilters.reduce((acc, filter) => {
            if (filter.type === 'range') {
                if (filter.values.min) acc[`${filter.id}MoreThan`] = Number(filter.values.min);
                if (filter.values.max) acc[`${filter.id}LowerThan`] = Number(filter.values.max);
            } else if (filter.type === 'composite' && filter.id === 'averageComparison') {
                acc = {
                    ...acc,
                    averageType: filter.values.averageType,
                    comparisonType: filter.values.comparisonType,
                    minPercentage: filter.values.minPercentage
                };

                if (filter.values.comparisonType === 'range') {
                    acc.maxPercentage = filter.values.maxPercentage;
                }
            } else {
                acc[filter.id] = filter.values;
            }
            return acc;
        }, {});

        if (filterData.marketCapMoreThan) filterData.marketCapMoreThan = Number(filterData.marketCapMoreThan);
        if (filterData.marketCapLowerThan) filterData.marketCapLowerThan = Number(filterData.marketCapLowerThan);
        if (filterData.priceMoreThan) filterData.priceMoreThan = Number(filterData.priceMoreThan);
        if (filterData.priceLowerThan) filterData.priceLowerThan = Number(filterData.priceLowerThan);

        onSearch(filterData);
    };

    const handleClear = () => {
        setActiveFilters([]);
        setSelectedFilters(new Set());
        onClear();
    };

    const renderRangeFilter = (filter) => {
        const ranges = predefinedRanges[filter.id];

        if (ranges) {
            return (
                <Stack spacing={1} sx={{ mt: 1 }}>
                    <FormControl fullWidth size="small">
                        <Select
                            value={filter.values?.min || ''}
                            onChange={(e) => handleFilterValueChange(filter.id, e.target.value, 'min')}
                            displayEmpty
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.mode === 'dark' 
                                        ? theme.palette.text.secondary 
                                        : theme.palette.text.disabled,
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme.palette.text.primary,
                                },
                                '& .MuiSelect-select': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        >
                            <MenuItem value="" sx={{ 
                                color: theme.palette.text.primary,
                                opacity: 0.7
                            }}>
                                Select minimum
                            </MenuItem>
                            {ranges.above.map((range) => (
                                <MenuItem
                                    key={range.label}
                                    value={range.value}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover
                                        }
                                    }}
                                >
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <Select
                            value={filter.values?.max || ''}
                            onChange={(e) => handleFilterValueChange(filter.id, e.target.value, 'max')}
                            displayEmpty
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.mode === 'dark' 
                                        ? theme.palette.text.secondary 
                                        : theme.palette.text.disabled,
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme.palette.text.primary,
                                },
                                '& .MuiSelect-select': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        >
                            <MenuItem value="" sx={{ 
                                color: theme.palette.text.primary,
                                opacity: 0.7
                            }}>
                                Select maximum
                            </MenuItem>
                            {ranges.below.map((range) => (
                                <MenuItem
                                    key={range.label}
                                    value={range.value}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover
                                        }
                                    }}
                                >
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            );
        }

        return (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                    size="small"
                    placeholder="Min"
                    value={filter.values.min}
                    onChange={(e) => handleFilterValueChange(filter.id, e.target.value, 'min')}
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            color: theme.palette.text.primary,
                            '& fieldset': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: theme.palette.text.secondary,
                        },
                    }}
                />
                <TextField
                    size="small"
                    placeholder="Max"
                    value={filter.values.max}
                    onChange={(e) => handleFilterValueChange(filter.id, e.target.value, 'max')}
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            color: theme.palette.text.primary,
                            '& fieldset': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: theme.palette.text.secondary,
                        },
                    }}
                />
            </Stack>
        );
    };

    const renderCompositeFilter = (filter) => {
        const { averageType, comparisonType, minPercentage, maxPercentage } = filter.values;

        return (
            <Stack spacing={1} sx={{ mt: 1 }}>
                <FormControl fullWidth size="small">
                    <Select
                        value={averageType || '50'}
                        onChange={(e) => handleFilterValueChange(filter.id, e.target.value, null, 'averageType')}
                        sx={{
                            color: theme.palette.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.text.primary,
                            },
                            '& .MuiSelect-select': {
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        {filter.components.averageType.options.map((option) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        bgcolor: theme.palette.action.hover
                                    }
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                    <Select
                        value={comparisonType || 'range'}
                        onChange={(e) => handleFilterValueChange(filter.id, e.target.value, null, 'comparisonType')}
                        sx={{
                            color: theme.palette.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.text.primary,
                            },
                            '& .MuiSelect-select': {
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        {filter.components.comparisonType.options.map((option) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        bgcolor: theme.palette.action.hover
                                    }
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                    <FormHelperText sx={{ color: theme.palette.text.secondary }}>
                        {comparisonType === 'range' ? 'Minimum percentage' : 'Percentage'}
                    </FormHelperText>
                    <Select
                        value={minPercentage || '0'}
                        onChange={(e) => handleFilterValueChange(filter.id, e.target.value, null, 'minPercentage')}
                        sx={{
                            color: theme.palette.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.text.primary,
                            },
                            '& .MuiSelect-select': {
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        {filter.components.percentageRange.options.map((option) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        bgcolor: theme.palette.action.hover
                                    }
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {comparisonType === 'range' && (
                    <FormControl fullWidth size="small">
                        <FormHelperText sx={{ color: theme.palette.text.secondary }}>
                            Maximum percentage
                        </FormHelperText>
                        <Select
                            value={maxPercentage || '0'}
                            onChange={(e) => handleFilterValueChange(filter.id, e.target.value, null, 'maxPercentage')}
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.mode === 'dark'
                                        ? theme.palette.text.secondary
                                        : theme.palette.text.disabled,
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme.palette.text.primary,
                                },
                                '& .MuiSelect-select': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        >
                            {filter.components.percentageRange.options
                                .filter(option => parseFloat(option.value) > parseFloat(minPercentage))
                                .map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            '&:hover': {
                                                bgcolor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                )}
            </Stack>
        );
    };

    const renderFilterContent = (filter) => {
        if (filter.type === 'range') {
            return renderRangeFilter(filter);
        }

        if (filter.type === 'composite') {
            return renderCompositeFilter(filter);
        }

        if (filter.type === 'select') {
            return (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <Select
                        value={filter.values || ''}
                        onChange={(e) => handleFilterValueChange(filter.id, e.target.value)}
                        displayEmpty
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    maxHeight: '200px',
                                    backgroundColor: theme.palette.background.paper,
                                    '& .MuiMenuItem-root': {
                                        color: theme.palette.text.primary,
                                    },
                                },
                            },
                        }}
                        sx={{
                            color: theme.palette.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.text.primary,
                            },
                            '& .MuiSelect-select': {
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        <MenuItem value="" sx={{ 
                            color: theme.palette.text.primary,
                            opacity: 0.7
                        }}>
                            Select {filter.label}
                        </MenuItem>
                        {filter.options?.map((option) => (
                            <MenuItem
                                key={option}
                                value={option}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        bgcolor: theme.palette.action.hover
                                    }
                                }}
                            >
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }
    };

    const filteredOptions = availableFilters.filter(filter =>
        filter.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ mb: 3 }}>
            <Paper
                variant='stats'
                sx={{
                    p: 2,
                    borderRadius: 1
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{
                        color: theme.palette.text.primary
                    }}>
                        {activeFilters.length} Filters
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAddFilterOpen(true)}
                        variant="contained"
                    >
                        Add Filters
                    </Button>
                </Stack>

                {activeFilters.length > 0 && (
                    <Grid container spacing={2}>
                        {activeFilters.map((filter) => (
                            <Grid item xs={12} sm={6} md={4} key={filter.id}>
                                <Paper
                                    variant='stats'
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="subtitle2" sx={{
                                            flex: 1,
                                            color: theme.palette.text.primary
                                        }}>
                                            {filter.label}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleFilter(filter)}
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                    {renderFilterContent(filter)}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Grid container spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                    {showExport && (
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                onClick={onExport}
                            >
                                Export
                            </Button>
                        </Grid>
                    )}
                    <Grid item>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleSearch}
                            startIcon={<SearchIcon />}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>

                <Dialog
                    open={isAddFilterOpen}
                    onClose={() => setIsAddFilterOpen(false)}
                    maxWidth="xs"
                    fullWidth
                    sx={{
                        '& .MuiDialog-container': {
                            backdropFilter: 'blur(4px)',
                        },
                        '& .MuiPaper-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(13, 34, 55, 0.95)'
                                : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            backgroundColor: 'transparent',
                            borderBottom: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'}`,
                            padding: '16px 24px'
                        }}
                    >
                        Add Filters
                        <IconButton
                            onClick={() => setIsAddFilterOpen(false)}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            backgroundColor: 'transparent',
                            padding: '20px 24px',
                        }}
                    >
                        <TextField
                            fullWidth
                            placeholder="Search filters..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? theme.palette.text.secondary
                                            : theme.palette.text.disabled,
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: theme.palette.text.secondary,
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <List sx={{ width: '100%' }}>
                            {filteredOptions.map((filter) => (
                                <ListItem
                                    key={filter.id}
                                    button
                                    onClick={() => toggleFilter(filter)}
                                    sx={{
                                        borderRadius: 1,
                                        mb: 0.5,
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover
                                        }
                                    }}
                                >
                                    <ListItemText primary={filter.label} />
                                    {selectedFilters.has(filter.id) && (
                                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                                            <CheckCircleIcon color="success" />
                                        </ListItemIcon>
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            backgroundColor: 'transparent',
                            borderTop: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'}`,
                            padding: '16px 24px',
                        }}
                    >
                        <Button
                            onClick={() => setIsAddFilterOpen(false)}
                            variant="contained"
                        >
                            Apply Filters
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    );
};

export default AvgScreenerFilterSystem;